import React, { useEffect, useState } from 'react';
import { Dashboard } from './components/Dashboard';
import DiscoverPage from './components/discover/DiscoverPage';
import { HabitSuggestion } from './services/aiSuggestionApi';
import { AuthPage } from './components/auth/AuthPage';
import { LandingPage } from './components/LandingPage';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsOfService } from './components/TermsOfService';
import { DeleteAccount } from './components/DeleteAccount';
import { ContactSupportPage } from './components/ContactSupportPage';
import { SubscriptionSuccess } from './components/subscription/SubscriptionSuccess';
import { SubscriptionCancel } from './components/subscription/SubscriptionCancel';
import { useAuthStore } from './store/authStore';
import { useTheme } from './hooks/useTheme';
import { Toaster } from 'react-hot-toast';

type AppView = 'landing' | 'auth' | 'dashboard' | 'discover' | 'subscription-success' | 'subscription-cancel' | 'privacy-policy' | 'terms-of-service' | 'delete-account' | 'contact-support';
type AuthMode = 'login' | 'register';

function App() {
  const { isAuthenticated, getCurrentUser, isLoading, token, isTokenExpired, silentLogout } = useAuthStore();
  const { resolvedTheme } = useTheme();
  const [currentView, setCurrentView] = useState<AppView>('auth');
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [hasInitialized, setHasInitialized] = useState(false);
  const [subscriptionSessionId, setSubscriptionSessionId] = useState<string | null>(null);
  const [pendingDiscoverySuggestion, setPendingDiscoverySuggestion] = useState<HabitSuggestion | null>(null);
  const [discoverResults, setDiscoverResults] = useState<HabitSuggestion[] | null>(null);

  // Check for special URL routes on app load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const pathname = window.location.pathname;

    if (pathname === '/subscription/success' && sessionId) {
      setSubscriptionSessionId(sessionId);
      setCurrentView('subscription-success');
      window.history.replaceState({}, document.title, '/');
    } else if (pathname === '/subscription/cancel') {
      setCurrentView('subscription-cancel');
      window.history.replaceState({}, document.title, '/');
    } else if (pathname === '/privacy-policy') {
      setCurrentView('privacy-policy');
    } else if (pathname === '/terms') {
      setCurrentView('terms-of-service');
    } else if (pathname === '/delete-account') {
      setCurrentView('delete-account');
    } else if (pathname === '/contact-support') {
      setCurrentView('contact-support');
    }
  }, []);

  useEffect(() => {
    const initializeAuth = async () => {
      // If we have a token, check if it's expired
      if (token) {
        if (isTokenExpired()) {
          // Token is expired, log out silently
          console.warn('Token expired on app initialization, logging out');
          silentLogout();
        } else {
          // Token is valid, try to get current user
          await getCurrentUser();
          
          // Load subscription data after successful auth initialization
          try {
            const subscriptionStore = await import('./store/subscriptionStore');
            subscriptionStore.useSubscriptionStore.getState().loadFeatureAccess();
            subscriptionStore.useSubscriptionStore.getState().loadSubscription();
          } catch (error) {
            console.error('Failed to load subscription data on app init:', error);
          }
        }
      }
      
      // Mark as initialized even if Apple callback is processing
      // The callback will update auth state and trigger view change
      setHasInitialized(true);
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodic token expiration check
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const checkTokenExpiration = () => {
      if (isTokenExpired()) {
        console.warn('Token expired during session, logging out');
        silentLogout();
      }
    };

    // Check every 30 seconds
    const interval = setInterval(checkTokenExpiration, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, token, isTokenExpired, silentLogout]);

  // Update view when authentication state changes, but only after initialization
  useEffect(() => {
    console.log('[App] Auth state changed - hasInitialized:', hasInitialized, 'isAuthenticated:', isAuthenticated, 'currentView:', currentView);

    if (!hasInitialized) return;

    // Don't redirect if on public pages that don't require auth
    const publicViews: AppView[] = ['privacy-policy', 'terms-of-service', 'delete-account', 'contact-support'];
    if (publicViews.includes(currentView)) {
      console.log('[App] On public page, skipping auth redirect');
      return;
    }

    if (isAuthenticated) {
      console.log('[App] User authenticated, switching to dashboard');
      setCurrentView('dashboard');
    } else {
      console.log('[App] User not authenticated, switching to auth');
      setCurrentView('auth');
    }
    // currentView is intentionally not a dependency - we only want to update based on auth state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, hasInitialized]);

  const handleLogin = () => {
    setAuthMode('login');
    setCurrentView('auth');
  };

  const handleRegister = () => {
    setAuthMode('register');
    setCurrentView('auth');
  };

  const handleAuthSuccess = () => {
    setCurrentView('dashboard');
  };

  const handleSubscriptionComplete = async () => {
    // Refresh subscription data before returning to dashboard
    try {
      const subscriptionStore = await import('./store/subscriptionStore');
      await Promise.all([
        subscriptionStore.useSubscriptionStore.getState().loadFeatureAccess(),
        subscriptionStore.useSubscriptionStore.getState().loadSubscription(),
      ]);
    } catch (error) {
      console.error('Failed to refresh subscription data:', error);
    }
    setCurrentView('dashboard');
    setSubscriptionSessionId(null);
  };

  const handleReturnToDashboard = () => {
    setCurrentView('dashboard');
  };

  const handleNavigateToDiscover = () => {
    setDiscoverResults(null);
    setCurrentView('discover');
  };

  const handleDiscoverySuggestionSelect = (suggestion: HabitSuggestion, suggestions: HabitSuggestion[]) => {
    setDiscoverResults(suggestions);
    setPendingDiscoverySuggestion(suggestion);
    setCurrentView('dashboard');
  };

  const handleDiscoverySuggestionConsumed = (saved: boolean) => {
    let remaining = discoverResults;
    if (saved && pendingDiscoverySuggestion) {
      remaining = discoverResults?.filter(s => s.name !== pendingDiscoverySuggestion.name) ?? null;
      setDiscoverResults(remaining);
    }
    setPendingDiscoverySuggestion(null);
    // If all suggestions have been saved, go to dashboard instead of discover
    if (saved && (!remaining || remaining.length === 0)) {
      setCurrentView('dashboard');
    } else {
      setCurrentView('discover');
    }
  };

  const handleShowPrivacyPolicy = () => {
    window.history.pushState({}, document.title, '/privacy-policy');
    setCurrentView('privacy-policy');
  };

  const handleBackFromPrivacyPolicy = () => {
    window.history.replaceState({}, document.title, '/');
    setCurrentView('auth');
  };

  const handleShowTermsOfService = () => {
    window.history.pushState({}, document.title, '/terms');
    setCurrentView('terms-of-service');
  };

  const handleBackFromTermsOfService = () => {
    window.history.replaceState({}, document.title, '/');
    setCurrentView('auth');
  };

  const handleShowDeleteAccount = () => {
    window.history.pushState({}, document.title, '/delete-account');
    setCurrentView('delete-account');
  };

  const handleBackFromDeleteAccount = () => {
    window.history.replaceState({}, document.title, '/');
    setCurrentView('auth');
  };

  const handleShowContactSupport = () => {
    window.history.pushState({}, document.title, '/contact-support');
    setCurrentView('contact-support');
  };

  const handleBackFromContactSupport = () => {
    window.history.replaceState({}, document.title, '/');
    setCurrentView('auth');
  };

  // Show loading only if we're still initializing or actively loading
  if (!hasInitialized || (isLoading && hasInitialized && isAuthenticated)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  const renderCurrentView = () => {
    switch (currentView) {
      case 'privacy-policy':
        return <PrivacyPolicy onBack={handleBackFromPrivacyPolicy} />;
      case 'terms-of-service':
        return <TermsOfService onBack={handleBackFromTermsOfService} />;
      case 'delete-account':
        return <DeleteAccount onBack={handleBackFromDeleteAccount} onLogin={handleLogin} />;
      case 'contact-support':
        return <ContactSupportPage onBack={handleBackFromContactSupport} />;
      case 'landing':
        return <LandingPage onLogin={handleLogin} onRegister={handleRegister} onPrivacyPolicy={handleShowPrivacyPolicy} onTermsOfService={handleShowTermsOfService} onDeleteAccount={handleShowDeleteAccount} onContactSupport={handleShowContactSupport} />;
      case 'auth':
        return (
          <AuthPage
            onSuccess={handleAuthSuccess}
            initialMode={authMode}
          />
        );
      case 'dashboard':
        return (
          <Dashboard
            onNavigateToDiscover={handleNavigateToDiscover}
            pendingDiscoverySuggestion={pendingDiscoverySuggestion}
            onDiscoverySuggestionConsumed={handleDiscoverySuggestionConsumed}
          />
        );
      case 'discover':
        return (
          <DiscoverPage
            onSuggestionSelect={handleDiscoverySuggestionSelect}
            onBack={() => setCurrentView('dashboard')}
            initialSuggestions={discoverResults ?? undefined}
          />
        );
      case 'subscription-success':
        return subscriptionSessionId ? (
          <SubscriptionSuccess 
            sessionId={subscriptionSessionId} 
            onComplete={handleSubscriptionComplete} 
          />
        ) : (
          <Dashboard
            onNavigateToDiscover={handleNavigateToDiscover}
            pendingDiscoverySuggestion={pendingDiscoverySuggestion}
            onDiscoverySuggestionConsumed={handleDiscoverySuggestionConsumed}
          />
        );
      case 'subscription-cancel':
        return <SubscriptionCancel onReturnToDashboard={handleReturnToDashboard} />;
      default:
        return <LandingPage onLogin={handleLogin} onRegister={handleRegister} onPrivacyPolicy={handleShowPrivacyPolicy} onTermsOfService={handleShowTermsOfService} onDeleteAccount={handleShowDeleteAccount} onContactSupport={handleShowContactSupport} />;
    }
  };

  return (
    <div className="App">
      {renderCurrentView()}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: resolvedTheme === 'dark' ? '#374151' : '#ffffff',
            color: resolvedTheme === 'dark' ? '#f9fafb' : '#111827',
            border: resolvedTheme === 'dark' ? '1px solid #4b5563' : '1px solid #e5e7eb',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#22c55e',
              secondary: resolvedTheme === 'dark' ? '#f9fafb' : '#ffffff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: resolvedTheme === 'dark' ? '#f9fafb' : '#ffffff',
            },
          },
        }}
      />
    </div>
  );
}

export default App;
